import { useState, useEffect, useCallback } from 'react';
import { getCookie, setCookie } from '../../cookieUtils.js';
import { fetchModelList } from '../../api/modelAPI.js';
import { getExplicitThinkingLevel } from '../../utils/modelThinking';

export const useModelManagement = (userEmail) => {
  const [model, setModel] = useState(getCookie('model') || 'default');
  const [customModelName, setCustomModelName] = useState('');
  const [showCustomModelInput, setShowCustomModelInput] = useState(model === 'finetuned');
  const [models, setModels] = useState([]);
  const [thinkingLevel, setThinkingLevel] = useState(getCookie('thinking_level') || '');

  useEffect(() => {
    let cancelled = false;
    const loadModels = async () => {
      const fetchedModels = await fetchModelList(userEmail);
      if (!cancelled) setModels(fetchedModels);
    };
    if (userEmail) loadModels();
    return () => { cancelled = true; };
  }, [userEmail]);

  useEffect(() => {
    setThinkingLevel(current => {
      const next = getExplicitThinkingLevel(models, model, current) || '';
      if (next !== current) setCookie('thinking_level', next);
      return next;
    });
  }, [models, model]);

  const handleModelChange = useCallback(event => {
    const selectedModel = event.target.value;
    setModel(selectedModel);
    setCookie('model', selectedModel);
    setShowCustomModelInput(selectedModel === 'finetuned');
    if (selectedModel !== 'finetuned') setCustomModelName('');
  }, []);

  const handleThinkingLevelChange = useCallback(event => {
    const next = event.target.value;
    setThinkingLevel(next);
    setCookie('thinking_level', next);
  }, []);

  const getSelectedModel = useCallback(
    () => model === 'finetuned' ? customModelName.trim() : model,
    [model, customModelName],
  );

  const getSelectedThinkingLevel = useCallback(
    () => getExplicitThinkingLevel(models, model, thinkingLevel),
    [models, model, thinkingLevel],
  );

  return {
    model,
    setModel,
    customModelName,
    setCustomModelName,
    showCustomModelInput,
    models,
    thinkingLevel,
    handleModelChange,
    handleThinkingLevelChange,
    getSelectedModel,
    getSelectedThinkingLevel,
  };
};
